import React, { useState, useEffect } from "react";
import { XIcon } from "@heroicons/react/outline";
import SlidingPane from "react-sliding-pane";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { BASE_URL } from "../../../constants";

const CurrencySlider = ({
  open,
  setOpen,
  selectedEntity,
  onCreate,
  onUpdate,
  setSelectedEntity,
}) => {
  const [formState, setFormState] = useState(false);
  const [currency, setCurrency] = useState(selectedEntity);

  useEffect(() => {
    setCurrency(selectedEntity);
  }, [selectedEntity]);

  const onChange = (e) => {
    setCurrency({ ...currency, [e.target.name]: e.target.value });
  };

  const onCheckBox = (e) => {
    setCurrency({ ...currency, [e.target.name]: e.target.checked });
  };

  const onSubmit = async () => {
    setFormState(true);
    try {
      const isNewBaseCurrency = currency.baseCurrency && currency.id === 0;
      // Only fetch exchange rates when updating existing currency from false to true
      const isUpdatedToBaseCurrency = currency.id !== 0 && 
                                      currency.baseCurrency === true && 
                                      selectedEntity.baseCurrency === false;
      
      if (currency.id !== 0) {
        const res = await axios.put(`${BASE_URL}/Currency`, currency);
        if (res.status === 200) {
          toast.success("Currency updated successfully!");
          onUpdate(currency);
          
          // Call exchange rate API ONLY if baseCurrency changed from false to true
          if (isUpdatedToBaseCurrency) {
            await fetchExchangeRates();
          }
        } else {
          toast.error("An error occurred while updating currency!");
        }
      } else {
        const res = await axios.post(`${BASE_URL}/Currency`, currency);
        onCreate(currency, res.data);
        setCurrency({ ...currency, id: res.data });
        toast.success("Currency added successfully!");
        
        // Call exchange rate API if new currency is added as base currency
        if (isNewBaseCurrency) {
          await fetchExchangeRates();
        }
      }

    } catch (e) {
      if (e.response?.status === 400) {
        toast.error(e.response.data);
      } else {
        toast.error("An error occurred while processing the request!");
      }
    } finally {
      setFormState(false);
    }
  };

  const fetchExchangeRates = async () => {
    try {
      toast.info("Fetching latest exchange rates...");
      const exchangeRateRes = await axios.post(`${BASE_URL}/api/ExchangeRate/fetch-latest`);
      if (exchangeRateRes.status === 200) {
        toast.success("Exchange rates fetched successfully!");
      } else {
        toast.warning("Exchange rates fetch completed with warnings.");
      }
    } catch (error) {
      console.error("Error fetching exchange rates:", error);
      toast.error("Failed to fetch exchange rates. You can manually fetch them later.");
    }
  };

  return (
    <SlidingPane
      closeIcon={
        <button
          type="button"
          className="rounded-md text-black hover:text-white focus:outline-none focus:ring-2 focus:ring-white absolute top-0"
        >
          <span className="sr-only">Close panel</span>
          <XIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      }
      hideHeader
      isOpen={open}
      onRequestClose={() => setOpen(false)}
      from="right"
      width="680px"
    >
      <div className="relative inset-0 overflow-hidden">
        <div className="w-screen max-w-2xl bg-white shadow-xl h-[87vh] overflow-y-scroll">
          <div className="px-4 py-4 bg-indigo-50 sm:px-6 sticky top-0">
            <div className="flex items-start justify-between space-x-3">
              <div className="space-y-1 text-2xl">
                {currency?.id !== 0 ? "Edit Currency" : "New Currency"}
              </div>
              <div className="h-7 flex items-center">
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-500"
                  onClick={() => setOpen(false)}
                >
                  <span className="sr-only">Close panel</span>
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          <form className="h-full flex flex-col">
            <div className="flex-1">
              <div className="py-4 space-y-6 sm:py-0 sm:space-y-0 sm:divide-y sm:divide-gray-200">
                <div className="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-4">
                  <div>
                    <label
                      htmlFor="code"
                      className="flex text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                    >
                      Code
                      <span className="block text-red-500 mt-0.5 pl-1">*</span>
                    </label>
                  </div>
                  <div className="sm:col-span-2">
                    <input
                      type="text"
                      name="code"
                      id="code"
                      required
                      className="block w-full shadow-sm sm:text-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md uppercase"
                      onChange={onChange}
                      value={currency?.code || ""}
                    />
                  </div>
                </div>

                <div className="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="flex text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                    >
                      Name
                      <span className="block text-red-500 mt-0.5 pl-1">*</span>
                    </label>
                  </div>
                  <div className="sm:col-span-2">
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      className="block w-full shadow-sm sm:text-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                      onChange={onChange}
                      value={currency?.name || ""}
                    />
                  </div>
                </div>

                <div className="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-4">
                  <div>
                    <label
                      className="flex text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                      htmlFor="isBaseCurrency"
                    >
                      Base Currency
                    </label>
                  </div>
                  <div className="sm:col-span-2">
                    <input
                      name="baseCurrency"
                      className="form-check-input appearance-none h-4 w-4 border border-gray-300 rounded-sm bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer"
                      type="checkbox"
                      id="isBaseCurrency"
                      onChange={onCheckBox}
                      checked={currency?.baseCurrency || false}
                    />
                  </div>
                </div>

                <div className="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-20 sm:px-6 sm:py-4">
                  <div className="flex justify-between form-check form-check-inline">
                    <label
                      className="form-check-label inline-block text-sm font-medium text-gray-900"
                      htmlFor="active"
                    >
                      Active
                    </label>
                    <input
                      name="isActive"
                      className="form-check-input appearance-none h-4 w-4 border border-gray-300 rounded-sm bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer"
                      type="checkbox"
                      id="active"
                      onChange={onCheckBox}
                      checked={currency?.isActive || false}
                    />
                  </div>
                </div>

                <div className="fixed bottom-0 w-full px-4 border-t border-gray-200 py-4 sm:px-6">
                  <div className="space-x-3 flex justify-end">
                    <button
                      type="button"
                      className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      onClick={() => setOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={onSubmit}
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-500 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none"
                      disabled={currency?.code === "" || currency?.name === ""}
                    >
                      {formState && (
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      )}
                      {currency?.id ? "Update" : "Create"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    </SlidingPane>
  );
};

export default CurrencySlider;